import {Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild} from '@angular/core';
import {forkJoin, Subject} from 'rxjs';
import {debounceTime, distinctUntilChanged, finalize, take, takeUntil} from 'rxjs/operators';
import {Sort} from '@angular/material/sort';
import {MatDialog, MatDialogRef} from '@angular/material/dialog';
import {MatSnackBar} from '@angular/material/snack-bar';
import {MatPaginator, PageEvent} from '@angular/material/paginator';
import {KeycloakService} from 'keycloak-angular';
import {Common, createNewPagedFilterDTO, getSortDirection} from '../../../../common/util/Util';
import {DateUtil} from '../../../../common/util/DateUtil';
import {PaymentsService} from '../../../services/payments.service';
import {PaymentDTO} from '../../../models/payment/PaymentDTO';
import {TableControl} from '../../../../common/models/TableControl';
import {PagedFilterDTO, PagedFilterDTONew} from '../../../../common/models/PagedFilterDTO';
import {
  UpdatePaymentReferenceDialogComponent
} from '../../dialogs/update-payment-reference-dialog/update-payment-reference-dialog.component';
import {PaymentReportDialogComponent} from '../../dialogs/payment-report-dialog/payment-report-dialog.component';
import {
  PreAuthorisationPaymentDialogComponent,
  PreAuthorisationPaymentDialogData
} from '../../dialogs/pre-authorisation-payment-dialog/pre-authorisation-payment-dialog.component';
import {PagedResponseDTO} from '../../../../common/models/PagedResponseDTO';
import {SharedService} from '../../../../common/services/shared-service';
import {PagedRequestDTO} from '../../../../common/models/PagedRequestDTO';
import {ClmRequestBuilder} from '../../../services/clm-request-builder';
import {PolicyDTO} from '../../../../ent/models/PolicyDTO';
import {PolicyService} from '../../../../ent/services/policy.service';
import {FormControl} from '@angular/forms';
import {CaseDTO} from '../../../models/CaseDTO';
import {CaseService} from '../../../services/case.service';
import {detailsExpand} from '../../../../common';
import {UserAuthorisationService} from 'src/app/modules/common/services/user-authorisation.service';
import {DisplayIcon} from '../../../../common/components';
import {EntityTable} from '../../../../common/types/entity-table.enum';
import {PaymentTypeFamily, PaymentTypeOption} from '../../dialogs/pre-authorisation-payment-dialog/payment-type.enum';
import {PaymentsPermissionService} from '../../../services/permission/payments-permission.service';
import {NameValueDTO} from '../../../../common/models/NameValueDTO';
import {LookupService} from '../../../../common/services/lookup.service';
import {LookupValue} from '../../../types/lookup-value.enum';

const ALL_PAYMENT_TYPES: string = 'All Payment Types';

enum TableColumns {
  Icons = 'Icons',
  PaymentId = 'paymentId',
  PaymentType = 'paymentType',
  IsImmediate = 'isImmediate',
  PaymentReference = 'paymentReference',
  PaymentExecutionDate = 'paymentExecutionDate',
  PaymentCompletionDate = 'paymentCompletionDate',
  Amount = 'amount',
}
@Component({
  selector: 'app-payments',
  templateUrl: './payments.component.html',
  styleUrls: [
    '../../../../../../styles/global/_management-styles.scss'
  ],
  animations: [detailsExpand]
})
export class PaymentsComponent implements OnChanges, OnInit, OnDestroy {
  readonly destroyed$ = new Subject();
  readonly dateFormat = DateUtil.dateFormat;
  readonly dateTimeFormat = DateUtil.dateTimeFormat;
  readonly debounceTime = Common.DEBOUNCE_TIME;
  readonly TableColumns = TableColumns;
  readonly paymentSearchTextChanged = new Subject<string>();
  readonly policyTextChanged = new Subject<string>();
  readonly caseTextChanged = new Subject<string>();
  readonly paymentTypeTextChanged = new Subject<string>();
  readonly DisplayIcon = DisplayIcon;
  readonly PaymentTypeOption = PaymentTypeOption;
  readonly PaymentTypeFamily = PaymentTypeFamily;

  length = 100;
  pageSize = 5;
  pageNumber = 0;
  pageSizeOptions: number[] = [5, 10, 25, 100];
  paymentExpandedRow: PaymentDTO | null;
  paymentDataSource: PaymentDTO[];
  paymentDisplayedColumns: string[] = Object.values(TableColumns);
  paymentTableControl: TableControl;
  policyFormControl: FormControl;
  caseFormControl: FormControl;

  userFirstLastName: string;
  paymentsReference: string;
  selectedStatus: string;
  selectedPolicy: PolicyDTO;
  selectedCase: CaseDTO;

  userRoles: string[];
  statuses: string[];
  filteredPolicies: PolicyDTO[];
  filteredCases: CaseDTO[];
  defaultPaymentType: string = ALL_PAYMENT_TYPES;
  selectedPaymentType: string;
  paymentTypeLookUpValues: NameValueDTO[];

  @Input() paymentsByCaseId: number;
  @ViewChild('paymentPaginator', {static: false}) paymentPaginator: MatPaginator;

  constructor(
    public dialog: MatDialog,
    private paymentsService: PaymentsService,
    private keycloakService: KeycloakService,
    private snackBar: MatSnackBar,
    private sharedService: SharedService,
    private lookupService: LookupService,
    private policyService: PolicyService,
    private caseService: CaseService,
    private userAuthorisationService: UserAuthorisationService,
    private paymentsPermissionService: PaymentsPermissionService
  ) {
    this.paymentTableControl = new TableControl(
      this.length,
      this.pageSize,
      this.pageNumber,
      this.pageSizeOptions,
      'paymentId',
      'desc',
      '',
      ''
    );
    this.statuses = ['Active Payments', 'Cancelled Payments', 'Manual Payments', 'All Payments'];
    this.selectedStatus = this.statuses[0];
    this.userFirstLastName = '';
    this.paymentsReference = '';
    this.policyFormControl = new FormControl('');
    this.caseFormControl = new FormControl('');
  }

  get showPaymentCancelButtons(): boolean {
    return (
      this.userAuthorisationService.hasRoleFinancialManager()
    );
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.paymentsByCaseId) {
      const caseId = changes.paymentsByCaseId.currentValue;
      this.getListOfPaymentsByCaseID(caseId);
    }
    else {
      this.getListOfPayments(this.paymentTableControl);
    }
  }

  ngOnInit(): void {
    if (!this.paymentsByCaseId) {
      this.getListOfPayments(this.paymentTableControl);
    }
    this.userRoles = this.keycloakService.getUserRoles();
    this.keycloakService.loadUserProfile().then(profile => {
      this.userFirstLastName = `${profile.firstName} ${profile.lastName}`;
    });

    this.initLookUpValues();
    this.initSubscriptions();
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  get isDataExist(): boolean {
    return !!(this.paymentDataSource && this.paymentDataSource?.length);
  }

  initLookUpValues(): void {
    forkJoin([this.lookupService.getListOfLookupValues(LookupValue.PaymentType)])
      .pipe(take(1))
      .subscribe({
        next: (response: NameValueDTO[][] ) => {
          if (response[0]) {
            response[0].unshift({value: null, name: ALL_PAYMENT_TYPES});
            this.paymentTypeLookUpValues = response[0];
          }
        },
        error: (errorResponse) => {
          const error = errorResponse.error;
          this.sharedService.openSnackBar(error.businessMessage, 'Dismiss');
        }
      });
  }
  initSubscriptions() {
    this.paymentSearchTextChanged.pipe(
      takeUntil(this.destroyed$),
      debounceTime(this.debounceTime),
      distinctUntilChanged()
    ).subscribe((searchString: string) => {

      // Search by reference or payment ID
      if (!isNaN(Number(searchString))) {
        this.paymentTableControl.filterValue = searchString;
      }

      this.paymentsReference = searchString;
      this.fetchPaymentsByCaseIdOrAllPayments(); // Payments step added case (funeral/hospital) to
      // fetch by ID or fetch all payments for this tab
    });

    this.policyTextChanged.pipe(
        takeUntil(this.destroyed$),
        debounceTime(this.debounceTime)
      )
      .subscribe((value: string) => {
          this.getListOfPolicies(value);
        });

    this.caseTextChanged.pipe(
      takeUntil(this.destroyed$),
      debounceTime(this.debounceTime)
    )
      .subscribe((value: string) => {
        this.getListOfCases(value);
      });

    this.paymentTypeTextChanged.pipe(
      takeUntil(this.destroyed$),
      debounceTime(this.debounceTime)
    )
      .subscribe((_: string) => {
        this.fetchPaymentsByCaseIdOrAllPayments();
      });
  }

  onInputChangedSearchPayments(value: string) {
    this.paymentSearchTextChanged.next(value);
  }

  onInputChangedSearchPolicies(value: string) {
    this.policyTextChanged.next(value);
  }

  onInputChangedSearchCases(value: string) {
    this.caseTextChanged.next(value);
  }

  statusSelectionChanged() {
    this.getListOfPayments(this.paymentTableControl);
  }


  onPolicySelected() {
    this.selectedPolicy = this.getPolicyDTOFromFilteredList();
    this.caseFormControl.disable();
    this.fetchPaymentsByCaseIdOrAllPayments();
  }

  onCaseSelected() {
    this.selectedCase = this.getCaseDTOFromFilteredList();
    this.policyFormControl.disable();
    this.fetchPaymentsByCaseIdOrAllPayments();
  }

  openPaymentDialog(event: MouseEvent, paymentDTO: PaymentDTO) {
    event.preventDefault();
    event.stopPropagation();

    const dialogRef = this.dialog.open(UpdatePaymentReferenceDialogComponent, {
      width: '40vw',
      data: paymentDTO
    });

    dialogRef.afterClosed().subscribe(() => {
      this.fetchPaymentsByCaseIdOrAllPayments();
    });
  }

  openReportDialog(paymentDTO: PaymentDTO, event: MouseEvent) {
    event.preventDefault();
    event.stopPropagation();

    this.dialog.open(PaymentReportDialogComponent, {
      width: '60vw',
      data: paymentDTO
    });

  }

  openPreAuthorisationPaymentDialog(paymentDTO: PaymentDTO, event: MouseEvent, paymentTypeOption?: PaymentTypeOption) {
    event.preventDefault();
    event.stopPropagation();


    const dialogData: any = {
      width: '40vw',
      data: {
        paymentDTO,
        paymentsByCaseId: this.paymentsByCaseId,
        paymentTypeOption
      } as PreAuthorisationPaymentDialogData,
    };


    if (paymentTypeOption == PaymentTypeOption.DuplicatePayment) {
      dialogData.data.paymentTypeFamily = PaymentTypeFamily.Duplicate;
    }
    else if (paymentTypeOption == PaymentTypeOption.UpfrontPayment) {
      dialogData.data.paymentTypeFamily = PaymentTypeFamily.Upfront;
    }
    else if (paymentTypeOption == PaymentTypeOption.UpfrontRefund || paymentTypeOption == PaymentTypeOption.ClaimRefund) {
      dialogData.data.paymentTypeFamily = PaymentTypeFamily.Refund;
    }

    const dialogRef: MatDialogRef<PreAuthorisationPaymentDialogComponent> = this.dialog.open(PreAuthorisationPaymentDialogComponent, dialogData);
    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.fetchPaymentsByCaseIdOrAllPayments();
      }
    });
  }

  clearPaymentClicked(paymentDTO: PaymentDTO, event: MouseEvent) {
    event.preventDefault();
    event.stopPropagation();

    paymentDTO.isActive = 0;
    paymentDTO.updated = new Date();
    paymentDTO.updatedBy = this.userFirstLastName;
    this.savePayment(paymentDTO);
  }

  reInstatePaymentClicked(paymentDTO: PaymentDTO, event: MouseEvent) {
    event.preventDefault();
    event.stopPropagation();

    paymentDTO.isActive = 1;
    paymentDTO.updated = new Date();
    paymentDTO.updatedBy = this.userFirstLastName;
    this.savePayment(paymentDTO);
  }

  getListOfPayments(tableControl: TableControl): void {
    const pagedRequestDTO = new PagedRequestDTO();
    pagedRequestDTO.entityClass = EntityTable.TblPayment;
    pagedRequestDTO.pageNumber = tableControl.pageNumber;
    pagedRequestDTO.pageSize = tableControl.pageSize;
    pagedRequestDTO.sortField = tableControl.sortField;
    pagedRequestDTO.sortDirection = getSortDirection(tableControl.sortDirection);
    pagedRequestDTO.fieldMap = {};

    if (this.selectedStatus !== this.statuses[3]) {
      let filterValue = 0;
      switch (this.selectedStatus) {
        case this.statuses[0]:
          filterValue = 1; // Active
          break;
        case this.statuses[1]:
          filterValue = 0; // Cancelled
          break;
        case this.statuses[2]:
          filterValue = 2; // Manual
          break;
        default:
          filterValue = 0;
          break;
      }
      const statusPagedFilterDTO = new PagedFilterDTO();
      statusPagedFilterDTO.name = 'isActive';
      statusPagedFilterDTO.operator = '==';
      statusPagedFilterDTO.value = filterValue.toString();
      pagedRequestDTO.fieldMap[`isActive`] = statusPagedFilterDTO;
    }

    if (this.selectedPaymentType && this.selectedPaymentType !== ALL_PAYMENT_TYPES) {
      const paymentTypeNameValueDTO: NameValueDTO = this.getPaymentTypeOption(this.selectedPaymentType);
      pagedRequestDTO.fieldMap[`paymentTypeByPaymentTypeId`] = createNewPagedFilterDTO('paymentTypeByPaymentTypeId.paymentTypeId', '==', String(paymentTypeNameValueDTO.value));
    }

    if (this.paymentTableControl != null && this.paymentTableControl.filterValue.trim()) {
      if (!isNaN(Number(this.paymentTableControl.filterValue))) {
        const paymentIdPagedFilterDTO = new PagedFilterDTONew();
        paymentIdPagedFilterDTO.name = 'paymentId';
        paymentIdPagedFilterDTO.value = this.paymentTableControl.filterValue.trim();
        paymentIdPagedFilterDTO.logicGate = 'AND';
        paymentIdPagedFilterDTO.bracket = '(';
        pagedRequestDTO.fieldMap[`paymentId`] = paymentIdPagedFilterDTO;
      }
      if (this.paymentsReference) {
        const paymentReferencePagedFilterDTO = new PagedFilterDTONew();
        paymentReferencePagedFilterDTO.name = 'paymentReference';
        paymentReferencePagedFilterDTO.value = this.paymentsReference;
        paymentReferencePagedFilterDTO.logicGate = 'OR';
        paymentReferencePagedFilterDTO.bracket = ')';
        pagedRequestDTO.fieldMap[`paymentReference`] = paymentReferencePagedFilterDTO;
      }
    }
    else if (this.paymentsReference) {
      const pagedFilterDTO = new PagedFilterDTO();
      pagedFilterDTO.name = 'paymentReference';
      pagedFilterDTO.value = this.paymentsReference;
      pagedRequestDTO.fieldMap[`paymentReference`] = pagedFilterDTO;
    }

    if (this.selectedPolicy) {
      const pagedFilterDTO = new PagedFilterDTO();
      pagedFilterDTO.name = 'policyNumber';
      pagedFilterDTO.value = this.selectedPolicy.number;
      pagedRequestDTO.fieldMap[`policyNumber`] = pagedFilterDTO;
    }

    if (this.selectedCase) {
      const pagedFilterDTO = new PagedFilterDTO();
      pagedFilterDTO.name = 'caseId';
      pagedFilterDTO.value = String(this.selectedCase.caseId);
      pagedRequestDTO.fieldMap[`caseId`] = pagedFilterDTO;
    }

    this.paymentsService.getListOfPayments(pagedRequestDTO)
      .pipe(
        take(1),
        finalize(() => {
          this.paymentPaginator ? this.paymentPaginator.disabled = false : null;
        })
      ).subscribe(
      (response: PagedResponseDTO<PaymentDTO>) => {
        this.paymentDataSource = response.resultList;
        this.paymentTableControl.length = response.totalRecords;
      },
      (errorResponse) => {
        const error = errorResponse.error;
        this.sharedService.openErrorDialog(error.businessMessage);
      }
    );
  }

  sortData($event: Sort) {
    if (!$event.direction) {
      return;
    }
    if (this.paymentPaginator) {
      this.paymentPaginator.firstPage();
    }
    this.paymentTableControl.pageNumber = 0;
    if ($event.active === 'assessingStatus') {
      this.paymentTableControl.sortField = 'adjudicationStatusByAdjudicationStatusId.name';
    }
    else if ($event.active === 'paymentType') {
      this.paymentTableControl.sortField = 'paymentTypeByPaymentTypeId.name';
    }
    else {
      this.paymentTableControl.sortField = $event.active;
    }
    this.paymentTableControl.sortDirection = $event.direction;

    this.fetchPaymentsByCaseIdOrAllPayments();
  }

  pageChanged($event: PageEvent) {
    this.paymentTableControl.pageNumber = $event.pageIndex;
    this.paymentTableControl.pageSize = $event.pageSize;
    this.fetchPaymentsByCaseIdOrAllPayments();
  }

  isPreAuthorisationPayment(payment: PaymentDTO) {
    return payment.casePayments != null && payment.casePayments.length > 0;
  }

  isImmediatePayment(payment: PaymentDTO) {
    return payment.isImmediate == 1;
  }

  getPolicyDisplayString(policy: PolicyDTO): string {
    return `${policy.name} - ${policy.number}`;
  }

  getCaseDisplayString(caseDTO: CaseDTO): string {
    return `${caseDTO.caseId}: ${caseDTO.name}`;
  }

  onBlurPolicySearch() {
    setTimeout(() => {
      if (
        this.getPolicyDTOFromFilteredList() == null
      ) {
        this.clearPolicy();
      }
    }, 1000);
  }

  private getPolicyDTOFromFilteredList() {
    return this.filteredPolicies != null ? this.filteredPolicies.find((item: PolicyDTO) => this.getPolicyDisplayString(item) === this.policyFormControl.value) : null;
  }

  onBlurCaseSearch() {
    setTimeout(() => {
      if (
        this.getCaseDTOFromFilteredList() == null
      ) {
        this.clearCase();
      }
    }, 1000);
  }


  private getCaseDTOFromFilteredList() {
    return this.filteredCases != null ? this.filteredCases.find((item: CaseDTO) => this.getCaseDisplayString(item) === this.caseFormControl.value) : null;
  }

  clearPolicy() {
    this.policyFormControl.reset();
    this.filteredPolicies = [];
    this.selectedPolicy = null;
    this.caseFormControl.enable();
    this.fetchPaymentsByCaseIdOrAllPayments();
  }

  clearCase() {
    this.caseFormControl.reset();
    this.filteredCases = [];
    this.selectedCase = null;
    this.policyFormControl.enable();
    this.fetchPaymentsByCaseIdOrAllPayments();
  }


  isUserAllowedTo(paymentTypeFamily: PaymentTypeFamily, paymentTypeOption?: PaymentTypeOption): boolean {
    return this.paymentsPermissionService.isUserAllowedToPayment(paymentTypeFamily, paymentTypeOption);
  }

  onPaymentTypeSelectionChanged(paymentType: string) {
    this.selectedPaymentType = this.getPaymentTypeOption(paymentType)?.name;
    this.paymentTypeTextChanged.next(paymentType);
  }

  private getListOfPaymentsByCaseID(caseId: number): void {
    this.paymentsService.getListOfPaymentsBy(caseId)
      .pipe(
        take(1),
      )
      .subscribe(
        (payments: PaymentDTO[]) => {
          this.paymentDataSource = payments;
          this.paymentTableControl.length = payments.length;
        },
        (errorResponse) => {
          const error = errorResponse.error;
          this.sharedService.openErrorDialog(error.businessMessage);
        }
      );
  }

  private fetchPaymentsByCaseIdOrAllPayments() {
    if (this.paymentsByCaseId) {
      this.getListOfPaymentsByCaseID(this.paymentsByCaseId);
    }
    else {
      this.getListOfPayments(this.paymentTableControl);
    }
  }

  private savePayment(paymentDTO: PaymentDTO): void {
    this.paymentsService.savePayment(paymentDTO)
      .pipe(
        takeUntil(this.destroyed$),
        finalize(() => {
          if (this.paymentsByCaseId) {
            this.getListOfPaymentsByCaseID(this.paymentsByCaseId);
          }
          else {
            this.getListOfPayments(this.paymentTableControl);
          }
        })
      )
      .subscribe(
      (response: PaymentDTO) => {
        this.snackBar.open(`Payment ${response.paymentId} cancelled successfully`, 'Dismiss');
      },
      (errorResponse) => {
        const error = errorResponse.error;
        this.sharedService.openErrorDialog(error.businessMessage);
      }
    );
  }

  private getListOfPolicies(searchValue: string): void {
    let pagedRequestDTO: PagedRequestDTO;
    pagedRequestDTO = ClmRequestBuilder.getFilteredListOfPoliciesPagedRequestDTO(searchValue);

    this.policyService.getListOfPolicies(pagedRequestDTO)
      .pipe(
        take(1),
      )
      .subscribe(
        (response: PagedResponseDTO<PolicyDTO>) => {
          response.resultList = response.resultList.map((policy: PolicyDTO) => {
            return policy;
          });
          this.filteredPolicies = response.resultList;
        },
        (errorResponse) => {
          const error = errorResponse.error;
          this.sharedService.openErrorDialog(error.businessMessage);
        }
      );
  }

  private getListOfCases(searchValue: string): void {
    let pagedRequestDTO: PagedRequestDTO;
    pagedRequestDTO = ClmRequestBuilder.getFilteredListOfCasesPagedRequestDTO(searchValue);

    this.caseService.getListOfCases(pagedRequestDTO)
      .pipe(
        take(1),
      )
      .subscribe(
        (response: PagedResponseDTO<CaseDTO>) => {
          if (response && response.resultList) {
            response.resultList = response.resultList.map((caseDTO: CaseDTO) => {
              return caseDTO;
            });
            this.filteredCases = response.resultList;
          }
        },
        (errorResponse) => {
          const error = errorResponse.error;
          this.sharedService.openErrorDialog(error.businessMessage);
        }
      );
  }

  private getPaymentTypeOption(paymentType: string): NameValueDTO {
    if (!this.paymentTypeLookUpValues.length) {
      return;
    }
    return this.paymentTypeLookUpValues.find((item: NameValueDTO) => item.name === paymentType);
  }

}
